import { createContext } from "react";
import type { RowSelectionState, Updater } from "@tanstack/react-table";
import { Counterparty } from "@common/types/counterparty";
import { DirectorySort } from "@common/constants/counterparty";
import { CounterpartyDirectoryView } from "@common/types/views/counterpartyDirectoryView";
import { View } from "@common/types/views/views";
import { noop } from "@common/helpers/function";
import { DirectoryFilters } from "./lib";
import { createDefaultCounterpartyDirectoryView } from "./counterpartyDirectoryViewConfig";
import { CounterpartyDirectoryViewRow } from "./counterpartyDirectoryViewTypes";

export interface GlDrawerState {
    counterpartyId: string | null;
    isNoCounterparty: boolean;
    /** Account code for account-level rows; when set, GL is scoped to this account. */
    accountCode?: number;
}

interface Context {
    filters: DirectoryFilters;
    setFilters: (filters: DirectoryFilters) => void;
    editedItem?: Counterparty;
    setEditedItem: (item?: Counterparty) => void;
    addingNew: boolean;
    setAddingNew: (addingNew: boolean) => void;
    counterpartySort: DirectorySort;
    setCounterpartySort: (sort: DirectorySort) => void;
    view: CounterpartyDirectoryView;
    onViewChange: (view: View) => void;
    selectedRow: CounterpartyDirectoryViewRow | null;
    setSelectedRow: (row: CounterpartyDirectoryViewRow | null) => void;
    glDrawerState: GlDrawerState | null;
    openGlDrawer: (
        counterpartyId: string | null,
        isNoCounterparty: boolean,
        accountCode?: number,
    ) => void;
    closeGlDrawer: () => void;
    rowSelection: RowSelectionState;
    setRowSelection: (updater: Updater<RowSelectionState>) => void;
    clearRowSelection: () => void;
}

export const DirectoryContext = createContext<Context>({
    selectedRow: null,
    setSelectedRow: noop,
    filters: {},
    setFilters: noop,
    setEditedItem: noop,
    addingNew: false,
    setAddingNew: noop,
    counterpartySort: DirectorySort.JOURNAL_ENTRY_LINES_DESC,
    setCounterpartySort: noop,
    view: createDefaultCounterpartyDirectoryView(),
    onViewChange: noop,
    glDrawerState: null,
    openGlDrawer: noop,
    closeGlDrawer: noop,
    rowSelection: {},
    setRowSelection: noop,
    clearRowSelection: noop,
});
